import React, { useEffect, useState } from "react";
import CreatableSelect from "react-select/creatable";
import { useTheme } from "../hooks/useTheme";
import SkeletonSelect from "./skeletons/SkeletonSelect";

const CustomCreatableSelect = ({
    value,
    options,
    isMulti = false,
    onChange,
    invalid,
    ...props
}) => {
    const theme = useTheme();
    const [isLoading, setIsLoading] = useState(true);

    const customStyles = {
        option: (styles, { isSelected }) => ({
            ...styles,
            fontFamily: "Hanken Grotesk, Sans-serif",
            fontWeight: isSelected ? "bold" : "normal",
            color: isSelected ? "#fff" : "",
            lineHeight: "0.8em",
        }),
    };

    const customTheme = {
        ...theme,
        colors: {
            ...theme.colors,
            primary25: "#B2D953",
            primary: theme === "dark-mode" ? "#2c414b" : "#dee2e6",
            neutral0: theme === "dark-mode" ? "#1b272d" : "#E4E4E4",
        },
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 2000); // 2s delay

        return () => clearTimeout(timer);
    }, []);

    // Handle skeleton while loading
    if (!options || options.length === 0) {
        if (isLoading) {
            return <SkeletonSelect />;
        } else {
            return <p>No options available</p>;
        }
    }

    // Sort alphabetically by label (works for grouped or flat)
    const sortedOptions = Array.isArray(options[0]?.options)
        ? options.map((group) => ({
            ...group,
            options: [...group.options].sort((a, b) =>
                a.label.toLowerCase().localeCompare(b.label.toLowerCase())
            ),
        }))
        : [...options].sort((a, b) =>
            a.label.toLowerCase().localeCompare(b.label.toLowerCase())
        );

    let selectedValue = null;

    // Ensure value matches option(s)
    if (options[0].options !== undefined) {
        // grouped
        for (const group of options) {
            const found = group.options.find((opt) => opt.value === value);
            if (found) {
                selectedValue = found;
            }
        }
    } else {
        // flat
        selectedValue =
            sortedOptions.find((option) => option.value === value) || null;
    }

    // If not found in options but we do have an external value, synthesize it
    if (!selectedValue && value != null && value !== "") {
        selectedValue = { value, label: String(value) };
    }

    return (
        <CreatableSelect
            {...props}
            options={sortedOptions}
            isMulti={isMulti}
            menuPortalTarget={document.body}
            isSearchable
            isClearable
            menuPosition="fixed"
            styles={{
                ...customStyles,
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({ ...base, zIndex: 9999 }),
            }}
            classNamePrefix="data-select"
            theme={customTheme}
            onChange={onChange}
            value={isMulti ? undefined : selectedValue}
            className={invalid ? "data-select--is-invalid" : ""}
        />
    );
};

export default CustomCreatableSelect;
